import { useEffect, useState } from 'react';
import { testPhoto, storeAndReadBack } from './SelfCheckScreen';
import { detectBarcodeInImage } from '../../platform/barcode';
import { phoneProof } from '../../platform/phoneKey';

/**
 * ENGINE CHECK (15 Oct). Owner: "I don't want to wake up one day and the app
 * does not work due to some browser update."
 *
 * Every night a robot opens this page in the current AND the coming versions of
 * Chrome and of Safari's engine (WebKit) and reads the results from
 * window.__engineCheck. It tests only what a browser update could break -- the
 * pieces a delivery is made of -- with no sign-in and no driver data:
 *   offline worker, saving a photo on the phone, camera, location, signature,
 *   barcode reading (a known sticker), reaching the office.
 * A driver or the office can open it too; it is the same test.
 * `?skip=camera,location` leaves out a check the robot's browser cannot fake.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

interface Check { key: string; ok: boolean; detail: string; ms: number }
declare global {
  interface Window { __engineCheck?: { done: boolean; checks: Check[]; ua: string } }
}

const LABEL: Record<string, string> = {
  offline_worker: 'Works offline (service worker)',
  save_photo: 'Saving a photo on the phone',
  phone_key: 'The phone lock key (one phone per driver)',
  camera: 'Camera',
  location: 'Location',
  signature: 'Signature',
  barcode: 'Reading a barcode',
  office: 'Connection to the office',
};

function within<T>(ms: number, p: Promise<T>): Promise<T> {
  return Promise.race([p, new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))]);
}
const why = (e: unknown) => String((e as Error)?.name && (e as Error).name !== 'Error' ? (e as Error).name : (e as Error)?.message ?? e).slice(0, 80);

async function camera(): Promise<string> {
  const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
  try {
    const v = document.createElement('video');
    v.muted = true; v.playsInline = true; v.srcObject = s;
    await v.play();
    await new Promise((r) => setTimeout(r, 600));
    const c = document.createElement('canvas');
    c.width = v.videoWidth || 320; c.height = v.videoHeight || 240;
    c.getContext('2d')?.drawImage(v, 0, 0, c.width, c.height);
    const b = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/jpeg', 0.8));
    if (!b) throw new Error('no picture from the camera');
    return `${c.width}x${c.height}, ${Math.round(b.size / 1024)} KB`;
  } finally {
    s.getTracks().forEach((t) => t.stop());
  }
}

function location(): Promise<string> {
  return new Promise((res, rej) => navigator.geolocation.getCurrentPosition(
    (p) => res(`±${Math.round(p.coords.accuracy)} m`),
    (e) => rej(new Error(`location error ${e.code}`)),
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }));
}

async function signature(): Promise<string> {
  const c = document.createElement('canvas');
  c.width = 400; c.height = 160;
  const g = c.getContext('2d');
  if (!g) throw new Error('no canvas');
  g.lineWidth = 3; g.beginPath(); g.moveTo(20, 120);
  for (let x = 20; x < 380; x += 20) g.lineTo(x, 60 + 40 * Math.sin(x / 30));
  g.stroke();
  const b = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/png'));
  if (!b) throw new Error('no signature image');
  return `${Math.round(b.size / 1024)} KB`;
}

async function barcodeRead(): Promise<string> {
  const img = new Image();
  img.src = '/check/robot-barcode.png';
  await img.decode();
  const v = await detectBarcodeInImage(img);
  if (v !== '123456') throw new Error(`read "${v}" instead of 123456`);
  return 'read 123456';
}

async function offlineWorker(): Promise<string> {
  if (!('serviceWorker' in navigator)) throw new Error('not supported');
  const r = await navigator.serviceWorker.ready;
  return r.active ? 'active' : 'registered';
}

async function office(): Promise<string> {
  const res = await fetch(`${API_BASE}/livez`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return 'answers';
}

export function EngineCheckScreen() {
  const [checks, setChecks] = useState<Check[]>([]);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const skip = (new URLSearchParams(window.location.search).get('skip') ?? '').split(',').filter(Boolean);
    const out: Check[] = [];
    window.__engineCheck = { done: false, checks: out, ua: navigator.userAgent };
    const run = async (key: string, fn: () => Promise<string>) => {
      const t0 = performance.now();
      if (skip.includes(key)) {
        out.push({ key, ok: true, detail: 'skipped', ms: 0 });
      } else {
        try { out.push({ key, ok: true, detail: await within(25_000, fn()), ms: Math.round(performance.now() - t0) }); }
        catch (e) { out.push({ key, ok: false, detail: why(e), ms: Math.round(performance.now() - t0) }); }
      }
      setChecks([...out]);
    };
    void (async () => {
      await run('offline_worker', offlineWorker);
      await run('save_photo', async () => `${(await storeAndReadBack(await testPhoto())).size} bytes back`);
      await run('phone_key', async () => `signed (${(await phoneProof('engine-check')).sig.length} chars)`);
      await run('camera', camera);
      await run('location', location);
      await run('signature', signature);
      await run('barcode', barcodeRead);
      await run('office', office);
      window.__engineCheck = { done: true, checks: out, ua: navigator.userAgent };
      setDone(true);
    })();
  }, []);

  const failed = checks.filter((c) => !c.ok).length;
  return (
    <main className="screen">
      <h1>Engine check</h1>
      <p className="muted small">Tests what a browser update could break. No sign-in, no driver data.</p>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {checks.map((c) => (
          <li key={c.key} style={{ margin: '6px 0' }}>
            <b style={{ color: c.ok ? '#15803d' : '#b00000' }}>{c.ok ? '✓' : '✗'}</b> {LABEL[c.key] ?? c.key}
            <span className="muted small"> — {c.detail}{c.ms ? ` (${c.ms} ms)` : ''}</span>
          </li>
        ))}
      </ul>
      <p data-engine-check={done ? (failed ? 'failed' : 'passed') : 'running'}>
        <b>{done ? (failed ? `${failed} check${failed > 1 ? 's' : ''} failed` : 'All checks passed') : 'Running…'}</b>
      </p>
    </main>
  );
}
